import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import { Star, BadgeCheck, ImagePlus, Loader2, X, ThumbsUp, ThumbsDown, PenLine } from "lucide-react";
import { Eyebrow, Lotus } from "./Ornaments";
import { Reveal } from "./Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SORTS = [
  { id: "most-recent", label: "Most Recent" },
  { id: "highest", label: "Highest Rated" },
  { id: "lowest", label: "Lowest Rated" },
];
const EMPTY = { name: "", email: "", title: "", content: "", rating: 0, photo: "" };

const Stars = ({ value, onChange, onHover, hover, testidPrefix, size = "w-8 h-8" }) => (
  <div className="flex gap-1">
    {[1, 2, 3, 4, 5].map((s) => (
      <button
        key={s}
        type="button"
        aria-label={`${s} star${s > 1 ? "s" : ""}`}
        data-testid={`${testidPrefix}-${s}`}
        onMouseEnter={() => onHover && onHover(s)}
        onMouseLeave={() => onHover && onHover(0)}
        onClick={() => onChange && onChange(s)}
        className="transition-transform duration-200 hover:scale-115 active:scale-95"
      >
        <Star className={`${size} ${s <= (hover || value) ? "fill-gold text-gold" : "text-gold/40"}`} />
      </button>
    ))}
  </div>
);

const fmtDate = (iso) => new Date(iso).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });

const ReviewCard = ({ r, onVote }) => {
  const voted = localStorage.getItem(`nexora_helpful_${r.id}`);
  return (
    <motion.article
      layout
      initial={{ opacity: 0, y: 24 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, scale: 0.97 }}
      transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
      data-testid={`review-card-${r.id}`}
      className="bg-white rounded-[26px] p-6 border border-terra/10"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-3">
          <span className="w-11 h-11 rounded-full bg-blush flex items-center justify-center font-display text-lg text-terra-deep shrink-0">{r.name.charAt(0).toUpperCase()}</span>
          <div>
            <p className="font-semibold text-ink flex items-center gap-1.5 flex-wrap">
              {r.name}
              {r.verified_buyer && (
                <span data-testid={`verified-badge-${r.id}`} className="inline-flex items-center gap-1 rounded-full bg-sage/15 text-sage-deep text-[11px] font-semibold px-2 py-0.5">
                  <BadgeCheck size={12} /> Verified Buyer
                </span>
              )}
            </p>
            <p className="text-xs text-muted-ink mt-0.5">{fmtDate(r.created_at)}</p>
          </div>
        </div>
        <Stars value={r.rating} hover={0} testidPrefix={`review-stars-${r.id}`} size="w-4 h-4" />
      </div>
      <h4 className="font-display text-xl text-ink mt-4">{r.title}</h4>
      <p className="text-sm text-muted-ink mt-2 leading-relaxed">{r.content}</p>
      {r.photo && (
        <a href={r.photo} target="_blank" rel="noreferrer" className="block mt-4">
          <img src={r.photo} alt={`Photo by ${r.name}`} data-testid={`review-photo-${r.id}`} className="rounded-2xl max-h-56 object-cover border border-terra/10" />
        </a>
      )}
      <div className="mt-5 pt-4 border-t border-terra/10 flex items-center justify-between gap-3 flex-wrap">
        <p className="text-xs text-muted-ink">Was this review helpful?</p>
        <div className="flex gap-2">
          {[["yes", "Yes", ThumbsUp], ["no", "No", ThumbsDown]].map(([v, label, Icon]) => (
            <button
              key={v}
              type="button"
              disabled={!!voted}
              data-testid={`helpful-${v}-${r.id}`}
              onClick={() => onVote(r.id, v)}
              className={`inline-flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-xs font-semibold border transition-colors duration-300 ${voted ? "opacity-70 cursor-default border-terra/15 text-muted-ink" : "border-terra/25 text-ink hover:bg-blush hover:border-terra"}`}
            >
              <Icon size={13} /> {label} ({v === "yes" ? r.helpful_yes : r.helpful_no})
            </button>
          ))}
        </div>
      </div>
    </motion.article>
  );
};

export const Reviews = () => {
  const [reviews, setReviews] = useState([]);
  const [sort, setSort] = useState("most-recent");
  const [form, setForm] = useState(EMPTY);
  const [hover, setHover] = useState(0);
  const [errors, setErrors] = useState({});
  const [photoErr, setPhotoErr] = useState("");
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);

  const load = async () => {
    try {
      const { data } = await axios.get(`${API}/reviews`);
      setReviews(data);
    } catch { toast.error("Could not load reviews."); } finally { setLoaded(true); }
  };
  useEffect(() => { load(); }, []);

  const sorted = useMemo(() => {
    const l = [...reviews];
    if (sort === "highest") l.sort((a, b) => b.rating - a.rating || b.created_at.localeCompare(a.created_at));
    else if (sort === "lowest") l.sort((a, b) => a.rating - b.rating || b.created_at.localeCompare(a.created_at));
    else l.sort((a, b) => b.created_at.localeCompare(a.created_at));
    return l;
  }, [reviews, sort]);

  const set = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const pickPhoto = (e) => {
    const f = e.target.files && e.target.files[0];
    if (!f) return;
    if (!f.type.startsWith("image/")) { setPhotoErr("Please choose an image file (JPG or PNG)."); return; }
    if (f.size > 4 * 1024 * 1024) { setPhotoErr("Image must be under 4 MB."); return; }
    setPhotoErr("");
    const url = URL.createObjectURL(f);
    const img = new Image();
    img.onload = () => {
      const max = 1000, sc = Math.min(1, max / Math.max(img.width, img.height));
      const c = document.createElement("canvas");
      c.width = Math.round(img.width * sc); c.height = Math.round(img.height * sc);
      c.getContext("2d").drawImage(img, 0, 0, c.width, c.height);
      setForm((v) => ({ ...v, photo: c.toDataURL("image/jpeg", 0.85) }));
      URL.revokeObjectURL(url);
    };
    img.onerror = () => setPhotoErr("That file could not be read as an image.");
    img.src = url;
  };

  const validate = () => {
    const er = {};
    if (form.rating < 1) er.rating = "Please pick a star rating.";
    if (form.name.trim().length < 2) er.name = "Please enter your full name.";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) er.email = "Please enter a valid email address.";
    if (form.title.trim().length < 3) er.title = "Give your review a short title.";
    if (form.content.trim().length < 10) er.content = "Tell us a little more (at least 10 characters).";
    setErrors(er);
    return !Object.keys(er).length;
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!validate()) { toast.error("Please fix the highlighted fields."); return; }
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/reviews`, { ...form, name: form.name.trim(), title: form.title.trim(), content: form.content.trim() });
      setReviews((l) => [data, ...l]);
      setForm(EMPTY); setHover(0);
      toast.success("Thank you for sharing your experience!");
    } catch (err) {
      toast.error(err?.response?.status === 422 ? "Please check your details and try again." : "Could not submit your review. Please try again.");
    } finally { setLoading(false); }
  };

  const vote = async (id, v) => {
    if (localStorage.getItem(`nexora_helpful_${id}`)) return;
    try {
      const { data } = await axios.post(`${API}/reviews/${id}/helpful?vote=${v}`);
      localStorage.setItem(`nexora_helpful_${id}`, v);
      setReviews((l) => l.map((r) => (r.id === id ? { ...r, helpful_yes: data.helpful_yes, helpful_no: data.helpful_no } : r)));
      toast.success("Thanks for your feedback!");
    } catch { toast.error("Could not record your vote."); }
  };

  const err = (k) => errors[k] && <span className="text-xs text-terra-deep mt-1.5 block" data-testid={`review-error-${k}`}>{errors[k]}</span>;

  return (
    <section id="reviews" data-testid="reviews-section" className="py-24 sm:py-32 bg-cream jaali">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <Reveal className="text-center max-w-2xl mx-auto mb-14">
          <Eyebrow className="justify-center">Customer reviews</Eyebrow>
          <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl text-ink mt-5 leading-[1.05]">Stories from our <span className="italic text-terra">celebration families.</span></h2>
        </Reveal>
        <div className="grid lg:grid-cols-12 gap-8 lg:gap-12 items-start">
          <Reveal className="lg:col-span-5 lg:sticky lg:top-28">
            <form onSubmit={submit} data-testid="review-form" className="bg-white rounded-[32px] p-6 sm:p-8 border border-terra/10 shadow-[0_40px_80px_-50px_rgba(64,52,52,0.4)] flex flex-col gap-4" noValidate>
              <div>
                <span className="block text-xs font-semibold tracking-[0.15em] uppercase text-muted-ink mb-2">Your rating <span className="text-terra-deep">*</span></span>
                <Stars value={form.rating} hover={hover} onHover={setHover} onChange={(s) => setForm((f) => ({ ...f, rating: s }))} testidPrefix="review-star" />
                {err("rating")}
              </div>
              {[
                { k: "name", label: "Full Name", ph: "e.g. Priya Sharma", t: "text" },
                { k: "email", label: "Email", ph: "you@example.com", t: "email" },
                { k: "title", label: "Review Title", ph: "e.g. A perfect sangeet night", t: "text" },
              ].map((f) => (
                <label key={f.k} className="block">
                  <span className="block text-xs font-semibold tracking-[0.15em] uppercase text-muted-ink mb-2">{f.label} <span className="text-terra-deep">*</span></span>
                  <input
                    type={f.t}
                    name={f.k}
                    value={form[f.k]}
                    onChange={set}
                    placeholder={f.ph}
                    data-testid={`review-${f.k}-input`}
                    className="w-full rounded-2xl bg-cream border border-terra/20 px-4 py-3 text-ink placeholder:text-muted-ink/70 outline-none transition-[border-color,box-shadow] duration-300 focus:border-pink focus:ring-4 focus:ring-pink/25"
                  />
                  {err(f.k)}
                </label>
              ))}
              <label className="block">
                <span className="block text-xs font-semibold tracking-[0.15em] uppercase text-muted-ink mb-2">Your review <span className="text-terra-deep">*</span></span>
                <textarea rows={4} name="content" value={form.content} onChange={set} placeholder="How did your celebration go?" data-testid="review-content-input" className="w-full rounded-2xl bg-cream border border-terra/20 px-4 py-3 text-ink placeholder:text-muted-ink/70 outline-none transition-[border-color,box-shadow] duration-300 focus:border-pink focus:ring-4 focus:ring-pink/25" />
                {err("content")}
              </label>
              <div>
                <span className="block text-xs font-semibold tracking-[0.15em] uppercase text-muted-ink mb-2">Photo <span className="normal-case tracking-normal font-normal">(optional, JPG/PNG under 4 MB)</span></span>
                {form.photo ? (
                  <div className="relative inline-block" data-testid="review-photo-preview">
                    <img src={form.photo} alt="Upload preview" className="h-24 rounded-2xl border border-terra/15" />
                    <button type="button" data-testid="review-photo-remove" onClick={() => setForm((f) => ({ ...f, photo: "" }))} className="absolute -top-2 -right-2 w-7 h-7 rounded-full bg-ink text-cream flex items-center justify-center" aria-label="Remove photo"><X size={14} /></button>
                  </div>
                ) : (
                  <label className="flex items-center gap-3 rounded-2xl border border-dashed border-terra/30 bg-blush/50 px-4 py-4 cursor-pointer hover:border-terra transition-colors">
                    <ImagePlus size={20} className="text-terra-deep" />
                    <span className="text-sm text-muted-ink">Add a photo from your celebration</span>
                    <input type="file" accept="image/*" data-testid="review-photo-input" onChange={pickPhoto} className="hidden" />
                  </label>
                )}
                {photoErr && <span className="text-xs text-terra-deep mt-1.5 block" data-testid="review-error-photo">{photoErr}</span>}
              </div>
              <button type="submit" disabled={loading} data-testid="review-submit-button" className="btn-pink justify-center py-4 text-base mt-2 disabled:opacity-70">
                {loading ? <Loader2 size={18} className="animate-spin" /> : <PenLine size={17} />} Submit Review
              </button>
            </form>
          </Reveal>
          <div className="lg:col-span-7">
            <div className="flex gap-2 flex-wrap mb-8" role="tablist" data-testid="review-sort-options">
              {SORTS.map((s) => (
                <button
                  key={s.id}
                  role="tab"
                  aria-selected={sort === s.id}
                  data-testid={`review-sort-${s.id}`}
                  onClick={() => setSort(s.id)}
                  className={`relative rounded-full px-5 py-2.5 text-sm font-medium transition-colors duration-300 ${sort === s.id ? "text-ink" : "text-muted-ink hover:text-ink bg-blush/70"}`}
                >
                  {sort === s.id && <motion.span layoutId="review-sort" className="absolute inset-0 rounded-full bg-pink" transition={{ type: "spring", stiffness: 380, damping: 30 }} />}
                  <span className="relative">{s.label}</span>
                </button>
              ))}
            </div>
            <motion.div layout className="flex flex-col gap-5" data-testid="reviews-list">
              <AnimatePresence>
                {sorted.map((r) => <ReviewCard key={r.id} r={r} onVote={vote} />)}
              </AnimatePresence>
              {loaded && !sorted.length && (
                <div data-testid="reviews-empty-state" className="rounded-[26px] border border-dashed border-terra/30 bg-white/60 p-12 text-center">
                  <Lotus className="w-12 h-9 mx-auto" />
                  <p className="font-display text-2xl text-ink mt-4">No reviews yet</p>
                  <p className="text-sm text-muted-ink mt-2">Be the first to share your Nexora experience — your story helps other families celebrate with confidence.</p>
                </div>
              )}
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
};
